import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { MessageDescriptor } from '@formatjs/intl';
import { Box, xcss } from '@atlaskit/primitives';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge';
import type { SmartLinkResponse } from '@atlaskit/linking-types';
import { useIntl } from '@atlassian/jira-intl';
import { GoalIcon } from '@atlassian/goals';
import { normalizeGoalAppearance } from '@atlassian/goals/icon';
import { fg } from '@atlassian/jira-feature-gating';
import { useGoalUrlConstructor } from '@atlassian/jira-issue-field-goals-common/src/index.tsx';
import { SimpleInlineSmartLink } from '@atlassian/jira-issue-field-smart-link/src/ui/simple-inline-smart-link/index.tsx';
import type { GoalGroupHeader$key } from './__generated__/GoalGroupHeader.graphql';
import messages from './messages.tsx';
import { generatePlaceholderData } from './generate-placeholder-data.tsx';

type Props = {
	goalGroupHeader: GoalGroupHeader$key | null;
};

const goalStatusToLabelMap: Record<string, MessageDescriptor> = {
	ARCHIVED: messages.statusLabelArchived,
	PENDING: messages.statusLabelPending,
	IN_PROGRESS: messages.statusLabelInProgress,
	COMPLETED: messages.statusLabelCompleted,
	DONE: messages.statusLabelCompleted,
	ON_TRACK: messages.statusLabelOnTrack,
	PAUSED: messages.statusLabelPaused,
	AT_RISK: messages.statusLabelAtRisk,
	OFF_TRACK: messages.statusLabelOffTrack,
	CANCELLED: messages.statusLabelCancelled,
};

const goalStatusToLozengeAppearanceMap: Record<string, ThemeAppearance> = {
	ARCHIVED: 'default',
	PENDING: 'default',
	IN_PROGRESS: 'inprogress',
	COMPLETED: 'success',
	DONE: 'success',
	ON_TRACK: 'success',
	PAUSED: 'moved',
	AT_RISK: 'moved',
	OFF_TRACK: 'removed',
	CANCELLED: 'removed',
};

export const GoalGroupHeader = ({ goalGroupHeader }: Props) => {
	const constructGoalUrl = useGoalUrlConstructor();

	const data = useFragment<GoalGroupHeader$key>(
		graphql`
			fragment GoalGroupHeader on JiraJqlGoalsFieldValue
			@argumentDefinitions(
				isGoalSmartLinkEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/goal-group-header-smart-link.relayprovider"
				}
			) {
				displayName
				goal {
					id @include(if: $isGoalSmartLinkEnabled)
					key @include(if: $isGoalSmartLinkEnabled)
					name @include(if: $isGoalSmartLinkEnabled)
					status
					score
				}
			}
		`,
		goalGroupHeader,
	);

	const { formatMessage } = useIntl();

	const displayName =
		data?.displayName ??
		formatMessage(
			fg('jira-issue-terminology-refresh-m3')
				? messages.noGoalsDisplayNameIssueTermRefresh
				: messages.noGoalsDisplayName,
		);
	const status = formatMessage(goalStatusToLabelMap[data?.goal?.status || 'PENDING']);
	const appearance = normalizeGoalAppearance(data?.goal?.status || 'PENDING');

	const goalUrl = useMemo<string | undefined>(
		() =>
			data?.goal.id && data?.goal.key && fg('goal_group_header_smart_link')
				? constructGoalUrl({ ari: data?.goal.id, key: data?.goal.key })
				: undefined,
		[constructGoalUrl, data?.goal.id, data?.goal.key],
	);

	const placeholderDataForSmartCard = useMemo<SmartLinkResponse | undefined>(() => {
		return goalUrl && data?.goal?.name && data?.goal?.status && fg('goal_group_header_smart_link')
			? generatePlaceholderData(goalUrl, data?.goal?.name, data?.goal?.status)
			: undefined;
	}, [data?.goal?.name, data?.goal?.status, goalUrl]);

	if (goalUrl && fg('goal_group_header_smart_link')) {
		return (
			<SimpleInlineSmartLink
				anchorTarget="_blank"
				url={goalUrl}
				placeholderData={placeholderDataForSmartCard}
				isSimplifiedUI
			/>
		);
	}

	return (
		<>
			{data?.goal && <GoalIcon size="16" appearance={appearance} goalIconKey="GOAL" />}
			<Box xcss={goalTextStyles}>{displayName}</Box>
			{data?.goal && (
				<Lozenge appearance={goalStatusToLozengeAppearanceMap[data.goal.status || 'PENDING']}>
					{status}
					{data.goal.score && ` - ${data.goal.score || ''}`}
				</Lozenge>
			)}
		</>
	);
};

const goalTextStyles = xcss({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
